// Loads the Google Maps JS API (with the Places library) exactly once, no
// matter how many components ask for it — LocationPicker is used from
// AdminVenues, the customer Venues page, and the Landing page, and each of
// those mounting shouldn't inject the <script> tag again.
//
// Needs REACT_APP_GOOGLE_MAPS_API_KEY in the web app's .env (a browser key,
// restricted in Google Cloud Console to your site's domain via an HTTP
// referrer restriction, with the Maps JavaScript API, Places API, and
// Geocoding API enabled). See the package README for the full setup steps.

let loadPromise = null;

export function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error('missing-key'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-maps-script');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('script-load-failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      loadPromise = null; // let a retry (e.g. after the network recovers) try again
      reject(new Error('script-load-failed'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

// Best-effort readable city/locality out of a Places `address_components`
// array — used to keep the admin form's separate "City" field populated
// without asking the admin to type it twice.
export function extractCity(addressComponents) {
  if (!Array.isArray(addressComponents)) return '';
  const byType = (type) => addressComponents.find((c) => c.types?.includes(type));
  const locality = byType('locality') || byType('administrative_area_level_2') || byType('postal_town');
  return locality?.long_name || '';
}

// State and PIN code from the same address_components — used by the venue
// form, where the admin can still type these by hand when the venue isn't
// on Google Maps.
export function extractStateAndPincode(addressComponents) {
  if (!Array.isArray(addressComponents)) return { state: '', pincode: '' };
  const byType = (type) => addressComponents.find((c) => c.types?.includes(type));
  return {
    state: byType('administrative_area_level_1')?.long_name || '',
    pincode: byType('postal_code')?.long_name || '',
  };
}
